import { type Surface } from '@shared/analytics/events';

/** Flow-loop surfaces — `is_flow_surface` flags these so funnels can isolate
 * the Flow subset from the app-wide pageview stream. */
const FLOW_SURFACES: ReadonlySet<Surface> = new Set<Surface>([
  'command_center',
  'playhouse',
  'action_view',
  'flow_shell',
  'queue',
  'pipeline',
]);

export interface PageSurface {
  page_name: string;
  surface: Surface;
  is_flow_surface: boolean;
}

/**
 * Map a pathname to a page_name + surface. App-wide (every route), so unknown
 * routes resolve to `other` rather than being dropped. Pure + exported.
 *
 * Lives in `lib/` (not the `usePageTracking` hook) so non-React callers can
 * reuse the SAME path→surface map without pulling in React/router deps — the
 * request interceptor (`lib/api.ts`) stamps `x-flow-surface` from it so the
 * server can attribute a queue/action event to the page the rep was on.
 * `usePageTracking` re-exports it for back-compat.
 */
export function routeSurface(pathname: string): PageSurface {
  const clean = pathname.replace(/\/+$/, '');
  const seg = clean.split('/').filter(Boolean);
  const head = seg[0];

  let page_name = head ?? 'home';
  let surface: Surface = 'other';

  if (seg.length === 0) {
    page_name = 'home';
    surface = 'other';
  } else if (head === 'accounts') {
    surface = 'command_center';
    page_name = seg.length > 1 ? 'account_detail' : 'accounts';
  } else if (head === 'pipeline') {
    surface = 'pipeline';
    page_name = seg.length > 1 ? 'pipeline_detail' : 'pipeline';
  } else if (head === 'categories') {
    surface = 'categories';
    page_name = 'categories';
  } else if (head === 'my-actions') {
    surface = 'my_actions';
    page_name = 'my_actions';
  } else if (head === 'library') {
    surface = 'library';
    page_name = 'library';
  } else if (head === 'flow') {
    // Every /flow/* path is the Flow shell — Playhouse / Action View were
    // retired (plan 2026-10-13-001 U3) and their URLs redirect into it.
    // `playhouse` / `action_view` stay in FLOW_SURFACES + SurfaceSchema for
    // analytics history only.
    surface = 'flow_shell';
    page_name = seg[1] ?? 'flow';
  } else if (head === 'tools') {
    surface = 'other';
    page_name = seg[1] ?? 'tools';
  } else if (head === 'admin') {
    surface = 'admin';
    page_name = seg[1] ? `admin_${seg[1]}` : 'admin';
  }

  return { page_name, surface, is_flow_surface: FLOW_SURFACES.has(surface) };
}
